import { AD_CREDIT } from "../enums/AdCredit";
import { ERequestMethods } from "../enums/RequestMethod";
import { request } from "../libs/request";
import {
  IAdCredit,
  ICreateAdCreditPackageDTO,
  IBodyRevertTransaction,
  IUerAdCreditHistory,
  IUpdateAdCreditDTO,
  IUserAdCredit,
  IUserAdCreditInfoGeneral,
  IUserAdCreditCollapseHistory,
  ECreditDeductionCategory,
  ECreditAddCategory,
} from "../types/AdCredit";
import { IXeroPayment } from "../types/Payment";
import {
  IRequestArgs,
  IResponseData,
  ListResponseData,
} from "../types/requests";

function getAdCredit({ queryParams }: IRequestArgs) {
  const query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
  };
  return request<ListResponseData<IAdCredit>>({
    method: ERequestMethods.GET,
    url: AD_CREDIT.MAIN,
    params: query,
  });
}

function getAdCreditPerUser() {
  return request<IResponseData<IUserAdCredit[]>>({
    method: ERequestMethods.GET,
    url: AD_CREDIT.AD_CREDIT_PRICE_PER_USER,
  });
}

function getUserAdCreditByAdmin(assignUserId: string) {
  const query = {
    assignUserId,
  };
  return request<IResponseData<IUserAdCredit[]>>({
    method: ERequestMethods.GET,
    url: AD_CREDIT.GET_USER_AD_CREDIT_BY_ADMIN,
    params: query,
  });
}

function getAdCreditInfoGeneral() {
  return request<IResponseData<IUserAdCreditInfoGeneral>>({
    method: ERequestMethods.GET,
    url: AD_CREDIT.GET_USER_AD_CREDIT,
  });
}
function getAdCreditInfoGeneralByUserId(userId: string, category?: string) {
  return request<IResponseData<IUserAdCreditInfoGeneral>>({
    method: ERequestMethods.GET,
    url: `${AD_CREDIT.GET_USER_AD_CREDIT}/${userId}`,
    params: category ? { filter: { category } } : undefined,
  });
}

function getUserAdCreditHistory({ queryParams }: IRequestArgs) {
  const query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
  };
  return request<ListResponseData<IUerAdCreditHistory>>({
    method: ERequestMethods.GET,
    url: AD_CREDIT.GET_USER_AD_CREDIT_HISTORY,
    params: query,
  });
}

function getUserAdCreditHistoryByAdmin({ userId }: { userId: string }) {
  return ({ queryParams }: IRequestArgs) => {
    const query = {
      sort: queryParams?.query?.sort,
      ...queryParams?.query?.search,
      ...queryParams?.pagination,
      filter: { userId },
    };
    return request<ListResponseData<IUerAdCreditHistory>>({
      method: ERequestMethods.GET,
      url: AD_CREDIT.GET_USER_AD_CREDIT_HISTORY_BY_ADMIN,
      params: query,
    });
  };
}

function getUserAdCreditCollapseHistory({ queryParams }: IRequestArgs) {
  const query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
  };
  return request<ListResponseData<IUserAdCreditCollapseHistory>>({
    method: ERequestMethods.GET,
    url: AD_CREDIT.GET_USER_AD_CREDIT_COLLAPSE_HISTORY,
    params: query,
  });
}

function getUserAdCreditCollapseHistoryByAdmin({ userId }: { userId: string }) {
  return ({ queryParams }: IRequestArgs) => {
    const query = {
      sort: queryParams?.query?.sort,
      ...queryParams?.query?.search,
      ...queryParams?.pagination,
      filter: { ...queryParams?.query?.filter[0], userId },
    };
    return request<ListResponseData<IUserAdCreditCollapseHistory>>({
      method: ERequestMethods.GET,
      url: AD_CREDIT.GET_USER_AD_CREDIT_COLLAPSE_HISTORY_BY_ADMIN,
      params: query,
    });
  };
}

function createAdCreditPackage(data: ICreateAdCreditPackageDTO) {
  return request({
    method: ERequestMethods.POST,
    url: AD_CREDIT.MAIN,
    data,
  });
}

function updatedCreditPackage(
  id: string,
  data: Partial<ICreateAdCreditPackageDTO>,
) {
  return request({
    method: ERequestMethods.PATCH,
    url: `${AD_CREDIT.MAIN}/${id}`,
    data,
  });
}

function createOder(data: { amount: number }) {
  return request<IResponseData<IXeroPayment>>({
    method: ERequestMethods.POST,
    url: AD_CREDIT.CREATE_ODER,
    data,
  });
}

function deleteAdCredit(id: string) {
  return request({
    method: ERequestMethods.DELETE,
    url: `${AD_CREDIT.MAIN}/${id}`,
  });
}

function addOrDeductUserCredit(data: {
  action: "add" | "deduct";
  updateData: IUpdateAdCreditDTO;
}) {
  if (data.action === "add") {
    return request({
      method: ERequestMethods.PUT,
      url: AD_CREDIT.ADD,
      data: data.updateData,
    });
  }
  return request({
    method: ERequestMethods.PUT,
    url: AD_CREDIT.DEDUCT,
    data: data.updateData,
  });
}

function getUserCreditHistory(userId: string) {
  return request<IResponseData<IUerAdCreditHistory[]>>({
    method: ERequestMethods.GET,
    url: `${AD_CREDIT.GET_USER_AD_CREDIT_HISTORY}/${userId}`,
  });
}

function revertTransaction(data: IBodyRevertTransaction) {
  return request<IResponseData<IUerAdCreditHistory[]>>({
    method: ERequestMethods.PUT,
    data,
    url: `${AD_CREDIT.REVERT_TRANSACTION}`,
  });
}

function revertAdCreditWithTransactionId(transactionId: string) {
  return request<IResponseData<IUerAdCreditHistory[]>>({
    method: ERequestMethods.PUT,
    url: AD_CREDIT.REVERT_SPECIFIC_TRANSACTION,
    data: { transactionId },
  });
}

function updateTransactionCategory(
  transactionId: string,
  payload: {
    category?: ECreditDeductionCategory | ECreditAddCategory;
    reason?: string;
  },
) {
  return request<IResponseData<IUerAdCreditHistory[]>>({
    method: ERequestMethods.PUT,
    url: AD_CREDIT.UPDATE_USER_CREDIT_CATEGORY,
    data: { historyId: transactionId, ...payload },
  });
}

const AdCreditRepository = Object.freeze({
  getAdCredit,
  getAdCreditPerUser,
  getUserAdCreditByAdmin,
  getAdCreditInfoGeneral,
  getAdCreditInfoGeneralByUserId,
  getUserAdCreditHistory,
  getUserAdCreditHistoryByAdmin,
  createAdCreditPackage,
  createOder,
  updatedCreditPackage,
  deleteAdCredit,
  addOrDeductUserCredit,
  getUserCreditHistory,
  revertTransaction,
  revertAdCreditWithTransactionId,
  getUserAdCreditCollapseHistory,
  getUserAdCreditCollapseHistoryByAdmin,
  updateTransactionCategory,
});

export default AdCreditRepository;
